import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { client } from '~/client';
import { CardSmallStatic } from '~/components/ui/CardSmall';
import { ReadValue } from '~/components/ui/ReadValue';
import { AssetDeploymentsDocument, AssetDeploymentsQuery } from '~/gql/graphql';
import { LONG_FORMAT, formatDate } from '~/helpers/formatDate';
import { useAsset } from './assets';

export async function loader({ params }: LoaderFunctionArgs) {
  invariant(params.assetId);
  const { data } = await client
    .query(AssetDeploymentsDocument, { id: params.assetId })
    .toPromise();

  return data?.asset?.deployments ?? [];
}

type DeploymentData = NonNullable<
  AssetDeploymentsQuery['asset']
>['deployments'][number];

export default function AssetDeployments() {
  const asset = useAsset();
  const deployments = useLoaderData() as Awaited<ReturnType<typeof loader>>;

  // Use deployment data directly
  const currentDeployment = asset?.deployment;

  if (!currentDeployment && !deployments.length) {
    return <div className='p-4'>This asset has no deployments</div>;
  }

  return (
    <>
      {currentDeployment && (
        <div className='p-3'>
          <p className='mb-3 block text-sm font-medium text-grey-50'>
            Current Deployment
          </p>
          <ReadValue label='Location'>
            {currentDeployment.displayLocation}
          </ReadValue>
          <ReadValue label='Deployed'>
            {formatDate(currentDeployment.startedAt, LONG_FORMAT)}
          </ReadValue>
        </div>
      )}

      {deployments.length > 0 && (
        <>
          <p className='mb-3 block px-3 text-sm font-medium text-grey-50'>
            Deployment History
          </p>
          {deployments.map((deployment: DeploymentData) => (
            <CardSmallStatic
              key={deployment.id}
              titleText={deployment.displayLocation}
              secondaryText={formatDate(deployment.startedAt, LONG_FORMAT)}
              image={deployment.image}
              status={deployment.status}
            />
          ))}
        </>
      )}
    </>
  );
}
